import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Check, Triangle } from 'lucide-react';
import {
  capLimits,
  round2,
  type Currency,
  type PlanDefinition,
  type PlanLimits,
  type PlanUsage,
  type SubscriptionPlan,
} from '@worki/shared';
import { billingApi } from '../lib/api.js';
import { currentLocale } from '../i18n.js';
import { money } from '../lib/format.js';
import { Modal } from './Modal.js';
import { useToast } from './Toast.js';
import { cn } from '../lib/cn.js';
import { useAgentCopy } from '../lib/agentCopy.js';

/**
 * Choosing a plan (AUTH-009).
 *
 * This was a screen of its own, reached from the account screen and listed in
 * the rail beside it. Both showed the plan you are on, so whichever one you
 * opened first told you nothing new — and the choice itself is three cards and
 * a button, which is a dialog's worth of screen. It opens over the account
 * screen instead, which is where the question is asked.
 *
 * Nothing here decides that money moved: checkout hands the customer to the
 * provider, and the plan changes when the webhook says so.
 */

/**
 * What each plan contains, in the order the account screen lists it.
 *
 * A price and a tagline were the whole card, which asked someone to pay three
 * times as much for "everything, no quotas" without saying what the quotas
 * were. These are the four caps the server enforces — the same rows, and the
 * same labels, as the usage list on the account screen.
 */
const LIMIT_ROWS: Array<keyof PlanLimits> = ['quotesPerMonth', 'voiceActionsPerMonth', 'galleries', 'connectedMailAccounts'];
const USAGE_KEYS: Record<keyof PlanLimits, keyof PlanUsage> = {
  quotesPerMonth: 'quotesThisMonth',
  voiceActionsPerMonth: 'voiceActionsThisMonth',
  galleries: 'galleries',
  connectedMailAccounts: 'connectedMailAccounts',
};
export function PlanDialog({
  open,
  onClose,
  plans,
  currentPlan,
  usage,
  currentLimits,
  currency,
  canBuy,
}: {
  open: boolean;
  onClose: () => void;
  plans: PlanDefinition[];
  currentPlan: SubscriptionPlan;
  usage: PlanUsage;
  currentLimits: PlanLimits;
  currency: Currency;
  /** An admin, on a deployment that has a payment provider wired up. */
  canBuy: boolean;
}) {
  const { t } = useTranslation();
  const agentCopy=useAgentCopy();
  const locale = currentLocale();
  const toast = useToast();
  const [pending, setPending] = useState<SubscriptionPlan | null>(null);

  const checkout = useMutation({
    mutationFn: (plan: SubscriptionPlan) => billingApi.checkout(plan),
    // A full navigation, not a route change: the customer leaves Worki entirely
    // and comes back through `/billing?status=`.
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: () => {
      setPending(null);
      toast.error(t('billing.checkoutFailed'));
    },
  });

  return (
    <Modal open={open} title={t('billing.changePlan')} onClose={onClose}>
      {/* One column, always. Three plans side by side needs 720px to stay
          readable, and this dialog is a bottom sheet for most of the people who
          open it — a phone would get three cards squeezed to a third each. */}
      <div className="grid gap-3">
        {plans.map((plan) => {
          const isCurrent = plan.id === currentPlan;
          const nameId = `plan-${plan.id}-name`;
          return (
            <section
              key={plan.id}
              aria-labelledby={nameId}
              className={cn(
                'rounded-xl border p-4 space-y-2',
                isCurrent ? 'border-primary bg-primary/5' : 'border-border bg-card',
              )}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span id={nameId} className="text-base font-bold text-foreground truncate">
                  {t(`billing.plans.${plan.id}`)}
                </span>
                {isCurrent && (
                  <span className="ms-auto shrink-0 text-[11px] font-medium px-2.5 py-1 rounded-full bg-primary text-primary-foreground flex items-center gap-1">
                    <Check className="h-3 w-3" />
                    {t('billing.currentBadge')}
                  </span>
                )}
              </div>

              {/* Prices are stored in agorot; the shared formatter owns the symbol
                  and its placement, which is not the same side in he and en. */}
              <p className="flex items-baseline gap-1.5">
                <span className="text-2xl font-bold text-foreground ltr-nums">
                  {money(round2(plan.monthlyPriceAgorot / 100), currency, locale)}
                </span>
                <span className="text-xs font-medium text-muted-foreground">{t('billing.perMonth')}</span>
              </p>

              <p className="text-xs text-muted-foreground">{t(`billing.planTagline.${plan.id}`)}</p>

              {/* The caps as the server will enforce them, not as the price list
                  was filled in: an "unlimited" galleries plan still stops at the
                  product's own ceiling, and printing ∞ next to a number the
                  server refuses is how a customer ends up on the phone. */}
              {/* Label at the start, number at the end — the shape the account
                  screen already uses for the same four caps, so the plan you
                  are on and the plan you are reading about line up. Written as
                  label-and-value rather than a sentence on purpose: "up to 1
                  mailboxes" needs Hebrew plural agreement for one, two, many and
                  other, and every one of those is a translation waiting to be
                  got wrong. */}
              <details className="group" open={isCurrent}>
                <summary className="flex items-center gap-2 cursor-pointer list-none text-xs font-semibold py-2 [&::-webkit-details-marker]:hidden">
                  <Triangle className="h-2.5 w-2.5 fill-current rotate-90 rtl:-rotate-90 group-open:rotate-180" />
                  {t('profile.planContents')}
                </summary>
              <ul className="text-xs text-muted-foreground space-y-1.5 pt-0.5">
                {LIMIT_ROWS.map((key) => {
                  const limit = (isCurrent ? currentLimits : capLimits(plan.limits))[key];
                  const used = usage[USAGE_KEYS[key]];
                  return (
                    <li key={key} className="flex items-baseline justify-between gap-2">
                      <span>{key==='voiceActionsPerMonth'?agentCopy('actionsPerUserMonth'):t(`billing.limitLabel.${key}`)}</span>
                      <span className="shrink-0 font-medium text-foreground">
                        {limit === null ? t('billing.unlimited') : limit === 0 ? t('billing.notIncluded') : isCurrent
                          ? `${t('billing.usageOf', { used, limit })} · ${t('billing.usageLeft', { left: Math.max(0, limit - used) })}`
                          : limit}
                      </span>
                    </li>
                  );
                })}
              </ul>
              </details>

              {!isCurrent && canBuy && (
                <button
                  type="button"
                  // Three identical "Choose this plan" buttons are one label read
                  // three times; the description names which is which.
                  aria-describedby={nameId}
                  disabled={checkout.isPending}
                  onClick={() => {
                    setPending(plan.id);
                    checkout.mutate(plan.id);
                  }}
                  className="w-full h-11 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  {pending === plan.id ? '…' : t('billing.choose')}
                </button>
              )}
            </section>
          );
        })}
      </div>
    </Modal>
  );
}
